import type {
  PostNordConditionalCost,
  PostNordCountry,
  PostNordDeliveryOptionsResponse,
  PostNordDeliveryOptionsResponseFE,
  PostNordDeliveryType,
  ShippingProviderConfig
} from 'ecosystem'
import { v4 as uuidv4 } from 'uuid'
import { isNullable } from 'shared-utils'

/**
 * Finds the matching conditional cost based on order amount and country
 */
const findMatchingConditionalCost = (
  conditionalCosts: PostNordConditionalCost[],
  cartTotalAmount: number,
  recipientCountryCode: PostNordCountry
): PostNordConditionalCost | undefined => {
  // Find costs that match the conditions or have no conditions (default)
  for (const conditionalCost of conditionalCosts) {
    const { conditions } = conditionalCost

    // If no conditions, this is a default cost (lowest priority)
    if (!conditions) {
      continue
    }

    // Check if conditions match
    const conditionsMatch = checkConditionsMatch(conditions, cartTotalAmount, recipientCountryCode)
    if (conditionsMatch) {
      return conditionalCost
    }
  }

  // If no conditional costs match, find the default one (no conditions)
  return conditionalCosts.find((cost) => !cost.conditions)
}

/**
 * Checks if the given conditions match the cart amount and country
 */
const checkConditionsMatch = (
  conditions: PostNordConditionalCost['conditions'],
  cartTotalAmount: number,
  recipientCountryCode: PostNordCountry
): boolean => {
  if (!conditions?.length) {
    return true
  }

  // Check if ANY condition in the array matches (OR logic)
  return conditions.some((condition) => {
    // Check country condition - if countries are specified, the recipient country must be in the list
    if (condition.countries && !condition.countries.includes(recipientCountryCode)) {
      return false
    }

    // Check order amount condition
    if (!isNullable(condition.orderAmount)) {
      const { from, to } = condition.orderAmount
      if (cartTotalAmount < from) {
        return false
      }
      if (!isNullable(to) && cartTotalAmount >= to) {
        return false
      }
    }

    return true
  })
}

export const generatePostnordDeliveryOptions = ({
  data,
  cartTotalAmount = 0,
  recipientCountryCode,
  shippingProviderConfig,
  allowedDeliveryTypes = []
}: {
  data: PostNordDeliveryOptionsResponse
  cartTotalAmount?: number
  shippingProviderConfig?: ShippingProviderConfig
  recipientCountryCode: PostNordCountry
  allowedDeliveryTypes?: PostNordDeliveryType[]
}): PostNordDeliveryOptionsResponseFE => {
  const calculateDeliveryCost = (deliveryType: string) => {
    const costs = shippingProviderConfig?.postnordOptions?.costs ?? []
    const fallbackMinimumOrderAmountForFreeDelivery =
      shippingProviderConfig?.simpleShipping?.minimumOrderAmountForFreeDelivery ?? -1
    const fallbackDeliveryFee = shippingProviderConfig?.simpleShipping?.deliveryFee ?? 0

    // Find cost configuration for the specific delivery type
    const deliveryTypeCost = costs.find((cost) => cost.deliveryType === deliveryType)

    // Find default cost configuration as fallback
    const defaultCost = costs.find((cost) => cost.deliveryType === 'default')

    // Find the matching conditional cost with proper fallback logic
    let matchingConditionalCost: PostNordConditionalCost | undefined

    if (deliveryTypeCost?.conditionalCosts?.length) {
      matchingConditionalCost = findMatchingConditionalCost(
        deliveryTypeCost.conditionalCosts,
        cartTotalAmount,
        recipientCountryCode
      )
    }

    if (!matchingConditionalCost && defaultCost?.conditionalCosts?.length) {
      matchingConditionalCost = findMatchingConditionalCost(
        defaultCost.conditionalCosts || [],
        cartTotalAmount,
        recipientCountryCode
      )
    }

    const deliveryFee = matchingConditionalCost?.deliveryFee ?? fallbackDeliveryFee
    const minimumOrderAmountForFreeDelivery =
      matchingConditionalCost?.minimumOrderAmountForFreeDelivery ??
      fallbackMinimumOrderAmountForFreeDelivery

    // If minimumOrderAmountForFreeDelivery is negative, free delivery is not available
    if (minimumOrderAmountForFreeDelivery < 0) {
      return deliveryFee
    }

    return cartTotalAmount >= minimumOrderAmountForFreeDelivery ? 0 : deliveryFee
  }

  ;(data as PostNordDeliveryOptionsResponseFE).warehouseToDeliveryOptions.forEach((item) => {
    item.deliveryOptions.forEach((deliveryOption) => {
      // Add id so that would be easier to manipulate with items
      deliveryOption.id =
        deliveryOption.defaultOption?.bookingInstructions.deliveryOptionId ??
        deliveryOption.additionalOptions?.[0]?.bookingInstructions.deliveryOptionId ??
        uuidv4()

      deliveryOption.price = calculateDeliveryCost(deliveryOption.type)
    })

    if (allowedDeliveryTypes.length) {
      // Filter delivery options based on allowedDeliveryTypes
      item.deliveryOptions = item.deliveryOptions.filter((deliveryOption) =>
        allowedDeliveryTypes.includes(deliveryOption.type)
      )

      // Sort delivery options by the order specified in allowedDeliveryTypes
      item.deliveryOptions.sort((a, b) => {
        const indexA = allowedDeliveryTypes.indexOf(a.type)
        const indexB = allowedDeliveryTypes.indexOf(b.type)

        // If both types are in the order array, sort by their index
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }

        // If only one type is in the order array, prioritize it
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1

        // If neither type is in the order array, maintain original order
        return 0
      })
    }
  })

  return data as PostNordDeliveryOptionsResponseFE
}
